"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var data_structures_1 = require("@tableau/data-structures");
/**
 * The FractalRowStore enables tree-based data to be shown in a data grid. There can be multiple top-level
 * items, each with any hierarchy of items below it.
 *
 * Unlike the ArrayNestedRowStore, this does not support range selection.
 */
var FractalRowStore = /** @class */ (function () {
    /**
     * @param {RoseTree<T>} topLevelTrees - The full domain of items regardless of visibility (parent expand/collapse state).
     * @param {Set<String>} expandedItemKeys - The set of items that should initially be expanded
     */
    function FractalRowStore(topLevelTrees, expandedItemKeys) {
        /** Helper structure for quick lookups using keys. */
        this.rowsByKey = {};
        /** keys present in this set are items whose children should be visible. */
        this.expandedItemKeys = {};
        /** The rows representing the visible items in the grid (contains top level items and children of expanded parents) */
        this.dataGridRows = [];
        if (expandedItemKeys) {
            this.expandedItemKeys = expandedItemKeys;
        }
        this.setItems(topLevelTrees);
    }
    FractalRowStore.prototype.setItems = function (topLevelTrees) {
        var _this = this;
        this.topLevelTrees = topLevelTrees;
        this.topLevelTrees.forEach(function (topLevelTree) {
            topLevelTree.forEach(function (item) {
                var dataGridRow = _this.buildRowFromItem(item);
                _this.rowsByKey[_this.getKey(item)] = dataGridRow;
            });
        });
        // Pass the same expanded item keys in as "oldExpandedKeys" because expansion status
        // should be preserved even if items come and go.
        this.updateRowsForExpansion(this.expandedItemKeys);
    };
    /**
     * GROUP: Public functions
     */
    /**
     * @param {number} start - inclusive, not guaranteed to be a positive number
     * @param {number} end - inclusive
     * @param rowHandler - the callback function that is executed once the new rows are ready
     */
    FractalRowStore.prototype.getItems = function (start, end, rowHandler) {
        rowHandler(this.dataGridRows);
    };
    FractalRowStore.prototype.getDomainItem = function (key) {
        return this.rowsByKey[key].data;
    };
    /**
     * Overwrites the existing expanded item keys with the given set. Care should be taken not to
     * overwrite with a new set containing the same expansion states, which could cause unnecessary
     * re-rendering in the React component that uses this row store.
     * @param newExpandedItemKeys - The new items that should be expanded.
     */
    FractalRowStore.prototype.setExpandedItems = function (newExpandedItemKeys) {
        var oldExpandedItemKeys = this.expandedItemKeys;
        this.expandedItemKeys = newExpandedItemKeys;
        this.updateRowsForExpansion(oldExpandedItemKeys);
    };
    /**
     * Toggles the expansion status of the given item and returns the new set of expanded items.
     * @param key
     * @returns {Set<string>}
     */
    FractalRowStore.prototype.toggleExpansionForItemKey = function (key) {
        var _a;
        var oldExpandedItemKeys = this.expandedItemKeys;
        if (this.expandedItemKeys[key]) {
            var _b = this.expandedItemKeys, _c = key, removedKey = _b[_c], restOfKeys = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
            this.expandedItemKeys = restOfKeys;
        }
        else {
            this.expandedItemKeys = __assign(__assign({}, this.expandedItemKeys), (_a = {}, _a[key] = true, _a));
        }
        this.updateRowsForExpansion(oldExpandedItemKeys);
        return this.expandedItemKeys;
    };
    /**
     * For an item with a given key, sets the expansion status of all its ancestors to true so that
     * the item will be visible.
     * @param key
     */
    FractalRowStore.prototype.expandParentsOfItemKey = function (key) {
        var parentKeys = this.getAncestors(key);
        var newExpandedAncestors = {};
        var needsUpdate = false;
        for (var _i = 0, parentKeys_1 = parentKeys; _i < parentKeys_1.length; _i++) {
            key = parentKeys_1[_i];
            if (!this.expandedItemKeys[key]) {
                needsUpdate = true;
                newExpandedAncestors[key] = true;
            }
        }
        if (needsUpdate) {
            var newExpandedItemKeys = __assign(__assign({}, this.expandedItemKeys), newExpandedAncestors);
            this.setExpandedItems(newExpandedItemKeys);
            return newExpandedItemKeys;
        }
        else {
            return this.expandedItemKeys;
        }
    };
    FractalRowStore.prototype.getAncestors = function (key) {
        var parentKeys = [];
        var parentKey = key;
        while (parentKey) {
            parentKeys.push(parentKey);
            var parentItem = this.getDomainItem(parentKey);
            parentKey = parentItem ? this.getParentKey(parentItem) : undefined;
        }
        return parentKeys.slice(1);
    };
    /**
     * Returns the row index of the item with the given key, or undefined if it's not currently visible
     * @param key
     */
    FractalRowStore.prototype.getRowIndex = function (key) {
        return this.rowIndexByKey[key];
    };
    /**
     * GROUP: Internal functions
     */
    FractalRowStore.prototype.isExpanded = function (item) {
        return this.isKeyExpanded(this.getKey(item));
    };
    FractalRowStore.prototype.isKeyExpanded = function (key) {
        return !!this.expandedItemKeys[key];
    };
    /**
     * An item is visible if it's at the top level, or if its parent is visible and expanded.
     * We keep child items expanded even if their parents are collapsed so that the expansion tree is
     * remembered when the parent item is expanded again.
     */
    FractalRowStore.prototype.shouldBeVisible = function (item) {
        var parentItemKey = this.getParentKey(item);
        if (parentItemKey === undefined) {
            return true;
        }
        else {
            var parentItem = this.getDomainItem(parentItemKey);
            return this.isKeyExpanded(parentItemKey) && parentItem !== undefined && this.shouldBeVisible(parentItem);
        }
    };
    /**
     * Updates rows[] to account for expanded/collapsed items. Also updates the entries in rowsByKey
     * for all affected rows.
     */
    FractalRowStore.prototype.updateRowsForExpansion = function (oldExpandedItemKeys) {
        var _this = this;
        var flattenedVisibleItems = [];
        var rowIndexByKey = {};
        var rowIndex = 0;
        // Top-level items are always visible, because they have no parent to control their expansion
        var visibleItemTrees = this.topLevelTrees.map(function (tree) {
            return data_structures_1.RoseTreeFilters.filterSubtreeRoots(tree, function (item) { return _this.shouldBeVisible(item); });
        });
        var updateRowAndAddToArray = function (item) {
            var itemKey = _this.getKey(item);
            // Create a new row object iff the expansion status changed
            var row = _this.rowsByKey[itemKey];
            if (_this.expandedItemKeys[itemKey] !== oldExpandedItemKeys[itemKey]) {
                row = _this.buildRowFromItem(item);
                _this.rowsByKey[itemKey] = row;
            }
            flattenedVisibleItems.push(row);
            rowIndexByKey[itemKey] = rowIndex++;
        };
        visibleItemTrees.forEach(function (tree) { return tree.forEach(updateRowAndAddToArray, data_structures_1.RoseTreeIterators.preOrderTraversal); });
        this.dataGridRows = flattenedVisibleItems;
        this.rowIndexByKey = rowIndexByKey;
    };
    return FractalRowStore;
}());
exports.FractalRowStore = FractalRowStore;
//# sourceMappingURL=FractalRowStore.js.map